export interface BomLine {
  costCents: number;
  quantity: number;
}

/** Round per line: cost × quantity */
export function calculateLineCostCents(costCents: number, quantity: number): number {
  return Math.round(costCents * quantity);
}

/** Sum rounded line costs from BOM lines */
export function calculateBomCostCents(lines: BomLine[]): number {
  return lines.reduce((sum, line) => sum + calculateLineCostCents(line.costCents, line.quantity), 0);
}

export function formatDerivedPercentInput(value: number): string {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Keep only PT-BR percent characters while typing (e.g. 30 or 20,6). */
export function sanitizePercentInput(value: string): string {
  const normalized = value.replace(/\./g, ",");
  let result = "";
  let hasComma = false;
  let decimals = 0;

  for (const char of normalized) {
    if (char >= "0" && char <= "9") {
      if (hasComma) {
        if (decimals >= 2) continue;
        decimals += 1;
      }
      result += char;
      continue;
    }

    if (char === "," && !hasComma && result.length > 0) {
      hasComma = true;
      result += char;
    }
  }

  return result;
}

export function parsePercentInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatPercentInput(value?: number | null): string {
  if (value == null) return "";
  return String(value).replace(".", ",");
}

/** Product input when set; otherwise inherit store default */
export function resolvePercentValue(
  input: string,
  storeDefault?: number | null
): number | null {
  if (input.trim() !== "") {
    return parsePercentInput(input);
  }
  return storeDefault ?? null;
}

export function storeDefaultPlaceholder(value?: number | null): string {
  if (value == null) return "Usa padrão da loja";
  return `Padrão da loja: ${formatPercentInput(value)}`;
}

export type StorePricingMode = "markup_on_cost" | "target_net_margin";

export function pricingModeFromGraphql(
  mode?: "MARKUP_ON_COST" | "TARGET_NET_MARGIN" | null
): StorePricingMode {
  return mode === "MARKUP_ON_COST" ? "markup_on_cost" : "target_net_margin";
}

export function pricingModeToGraphql(
  mode: StorePricingMode
): "MARKUP_ON_COST" | "TARGET_NET_MARGIN" {
  return mode === "markup_on_cost" ? "MARKUP_ON_COST" : "TARGET_NET_MARGIN";
}
